'use client'

import React, { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from 'react'

interface PageAction {
  key: string
  label: string
  icon?: React.ReactNode
  onClick?: () => void
  type?: 'default' | 'primary' | 'text'
  size?: 'small' | 'middle' | 'large'
}

export interface PageInfo {
  title: string
  /**
   * Optional short subtitle for SubNavbar only.
   * Prefer page-body copy for narrative text — shell chrome should not
   * restate the same greeting/description the content area already shows.
   * Empty string or omit = no description in chrome.
   */
  description?: string
  breadcrumbs?: any[]
  showRefresh?: boolean
  showSettings?: boolean
  onRefresh?: () => void
  onSettings?: () => void
  actions?: PageAction[]
  badge?: number | string // Badge count or text to display
}

interface PageContextType {
  pageInfo: PageInfo
  setPageInfo: (info: PageInfo) => void
  timeRange: string
  setTimeRange: (range: string) => void
  autoRefresh: boolean
  setAutoRefresh: (refresh: boolean) => void
  isLoading: boolean
  setIsLoading: (loading: boolean) => void
}

const PageContext = createContext<PageContextType | undefined>(undefined)

interface PageProviderProps { children: ReactNode }

/** Normalize description: trim; empty → undefined (no chrome subtitle). */
function normalizeDescription(value: string | undefined | null): string | undefined {
  if (value == null) return undefined
  const trimmed = String(value).trim()
  return trimmed.length > 0 ? trimmed : undefined
}

export function PageProvider({ children }: PageProviderProps) {
  // No default description — breadcrumbs + title own location chrome
  const [pageInfo, setPageInfoState] = useState<PageInfo>({
    title: 'Dashboard',
    breadcrumbs: [{ label: 'Home', href: '/' }],
  })
  const [timeRange, setTimeRange] = useState('1D')
  const [autoRefresh, setAutoRefresh] = useState(true)
  const [isLoading, setIsLoading] = useState(false)

  const setPageInfo = useCallback((info: PageInfo) => {
    setPageInfoState({
      ...info,
      description: normalizeDescription(info.description),
    })
  }, [])

  return (
    <PageContext.Provider value={{ pageInfo, setPageInfo, timeRange, setTimeRange, autoRefresh, setAutoRefresh, isLoading, setIsLoading }}>
      {children}
    </PageContext.Provider>
  )
}

export function usePageInfo() {
  const context = useContext(PageContext)
  if (context === undefined) {
    throw new Error('usePageInfo must be used within a PageProvider')
  }
  return context
}

/**
 * Register shell page chrome (title, breadcrumbs, refresh).
 * Does **not** inject a default description — omit description or pass "" to
 * keep SubNavbar free of subtitle text that would duplicate page-body copy.
 *
 * Pass stable `onRefresh` / `onSettings` (useCallback) so the effect does not thrash.
 */
export function usePageSetup(pageInfo: Partial<PageInfo>) {
  const { setPageInfo } = usePageInfo()
  const lastPageInfoRef = useRef<string>('')

  const title = pageInfo.title
  const description = normalizeDescription(pageInfo.description)
  const showRefresh = pageInfo.showRefresh
  const showSettings = pageInfo.showSettings
  const badge = pageInfo.badge
  const onRefresh = pageInfo.onRefresh
  const onSettings = pageInfo.onSettings
  // Stable serializations for array/object identity (callers pass inline literals)
  const breadcrumbsKey = JSON.stringify(pageInfo.breadcrumbs ?? null)
  const actionsKey = JSON.stringify(
    (pageInfo.actions ?? []).map((a) => ({ key: a.key, label: a.label, type: a.type }))
  )
  const breadcrumbsRef = useRef(pageInfo.breadcrumbs)
  const actionsRef = useRef(pageInfo.actions)
  const onRefreshRef = useRef(onRefresh)
  const onSettingsRef = useRef(onSettings)
  breadcrumbsRef.current = pageInfo.breadcrumbs
  actionsRef.current = pageInfo.actions
  onRefreshRef.current = onRefresh
  onSettingsRef.current = onSettings

  const invokeRefresh = useCallback(() => onRefreshRef.current?.(), [])
  const invokeSettings = useCallback(() => onSettingsRef.current?.(), [])

  useEffect(() => {
    const pageInfoKey = [
      title ?? '',
      description ?? '',
      String(showRefresh ?? false),
      String(showSettings ?? false),
      String(badge ?? ''),
      String(Boolean(onRefresh)),
      String(Boolean(onSettings)),
      breadcrumbsKey,
      actionsKey,
    ].join('|')

    if (lastPageInfoRef.current === pageInfoKey) return

    lastPageInfoRef.current = pageInfoKey

    setPageInfo({
      title: title || 'Dashboard',
      description,
      breadcrumbs: breadcrumbsRef.current || [{ label: 'Home', href: '/' }],
      showRefresh,
      showSettings,
      actions: actionsRef.current,
      badge,
      onRefresh: onRefresh ? invokeRefresh : undefined,
      onSettings: onSettings ? invokeSettings : undefined,
    })
  }, [
    title,
    description,
    showRefresh,
    showSettings,
    badge,
    breadcrumbsKey,
    actionsKey,
    Boolean(onRefresh),
    Boolean(onSettings),
    invokeRefresh,
    invokeSettings,
    setPageInfo,
  ])
}


